import React, { FC, InputHTMLAttributes, useId } from 'react';
import { VariantProps } from 'class-variance-authority';
import SharedButton from '../../shared/ui/button';
import { Input as SharedInput } from '../../shared/ui/input';
import { Card as SharedCard } from '../../shared/ui/card';
import { cn } from '../../shared/lib/utils';

interface CardProps {
  children: React.ReactNode;
  className?: string;
}

export const Card: FC<CardProps> = ({ children, className = '' }) => (
  <SharedCard className={cn("p-6", className)}>
    {children}
  </SharedCard>
);

interface ButtonProps {
  children: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  className?: string;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
}

export const Button: FC<ButtonProps> = ({ 
  children, 
  onClick, 
  variant = 'primary', 
  size = 'default',
  className = '', 
  disabled = false, 
  type = 'button' 
}) => {
  const variantMap: Record<string, VariantProps<typeof SharedButton>["variant"]> = {
    primary: "default",
    secondary: "secondary",
    danger: "destructive",
    ghost: "ghost"
  };

  return (
    <SharedButton 
      type={type} 
      onClick={onClick} 
      variant={variantMap[variant] || "default"}
      size={size}
      className={className}
      disabled={disabled}
    >
      {children}
    </SharedButton>
  );
};

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  className?: string;
}

export const Input: FC<InputProps> = ({ label, className = '', id: providedId, ...props }) => {
  const generatedId = useId();
  const inputId = providedId || (label ? generatedId : undefined);
  return (
    <div className={cn("flex flex-col gap-1 w-full", className)}>
      {label && (
        <label
          htmlFor={inputId}
          className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider cursor-pointer"
        >
          {label}
        </label>
      )}
      <SharedInput id={inputId} {...props} />
    </div>
  );
};
